import { FC, useState } from 'react';

import LockOpenIcon from '@mui/icons-material/LockOpen';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, Button, Typography, useTheme } from '@mui/material';

interface RevealButtonProps {
    publicDescription?: string;
    isOneTimeLink?: boolean;
    onReveal: () => Promise<void>;
    isLoading?: boolean;
}

export const RevealButton: FC<RevealButtonProps> = ({
    publicDescription,
    isOneTimeLink = false,
    onReveal,
    isLoading = false,
}) => {
    const theme = useTheme();
    const [error, setError] = useState<string | null>(null);

    const handleReveal = async () => {
        setError(null);

        try {
            await onReveal();
        } catch (_err) {
            setError('Failed to reveal secret. Please try again.');
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                p: 3,
                maxWidth: 400,
                mx: 'auto',
            }}
        >
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main + '20',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <LockOpenIcon
                    sx={{
                        fontSize: 40,
                        color: theme.palette.primary.main,
                    }}
                />
            </Box>

            <Box sx={{ textAlign: 'center' }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 1,
                    }}
                >
                    Secret ready
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: theme.palette.text.secondary,
                    }}
                >
                    Click the button below to reveal the secret content.
                </Typography>
            </Box>

            {publicDescription && (
                <Alert severity="info" sx={{ width: '100%' }}>
                    {publicDescription}
                </Alert>
            )}

            {isOneTimeLink && (
                <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ width: '100%' }}>
                    This is a one-time link. Once revealed, it cannot be accessed again.
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            )}

            <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<LockOpenIcon />}
                onClick={handleReveal}
                disabled={isLoading}
                sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                }}
            >
                {isLoading ? 'Revealing...' : 'Reveal secret'}
            </Button>
        </Box>
    );
};
